import React, { SyntheticEvent } from 'react';
import styled from 'styled-components';
import { Focus } from '@fenderdigital/ui/utils/css';
import { Course, Lesson } from '@fenderdigital/api-definitions/play';
import HeartIcon from '@fenderdigital/ui/Icons/HeartBold';
import HeartIconFilled from '@fenderdigital/ui/Icons/HeartBoldFill';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux-types/root.redux-types';
import { setFavorite, unsetFavorite } from '../../actions/favorites';
import { EventNames, trackPageEvents } from '../../lib/analytics';

interface CardMenuProps {
  courseData?: Course;
  lessonData?: Lesson;
  isCourse?: boolean;
  isSong?: boolean;
  testId?: string;
}

const FavoriteButton = styled.button`
  cursor: pointer;
  ${Focus}
`;

const getFavoriteCategory = (lessonData?: Lesson): 'course' | 'lesson' | 'practice-sheet' => {
  if (!lessonData) return 'course';
  return lessonData['activity-type'];
};

const MarkFavorite: React.FC<CardMenuProps> = ({
  courseData,
  lessonData,
  isCourse = false,
  isSong,
  testId,
}) => {
  const dispatch = useDispatch();
  const selector = (state: RootState) => ({
    favoritesKeys: state.favorites.keys,
  });
  const { favoritesKeys } = useSelector(selector);
  const { id: courseId = '', slug: courseSlug, title: courseTitle } = courseData || {};
  const { id: lessonId = '', slug: lessonSlug, title: lessonTitle, type } = lessonData || {};
  const { user } = usePlayUser();
  const id = isCourse ? courseId : lessonId;
  const isFavorited = id in favoritesKeys || false;

  const category = getFavoriteCategory(lessonData);

  const handleFavorite = (event: SyntheticEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    // eslint-disable-next-line @typescript-eslint/camelcase
    trackPageEvents(EventNames.FAVORITES_ALERT, { fc_id: user?.userID });
    const sourcePath = isCourse ? 'my-path' : 'course-detail';
    const favoriteData = {
      slug: isCourse ? courseSlug : lessonSlug,
      name: isCourse ? courseTitle : lessonTitle,
      type,
    };
    if (isFavorited) {
      dispatch(
        unsetFavorite(
          isCourse ? courseId : lessonId,
          category,
          sourcePath,
          favoriteData,
          user?.userID,
        ),
      );
    } else {
      dispatch(
        setFavorite(
          isCourse ? courseId : lessonId,
          category,
          sourcePath,
          favoriteData,
          user?.userID,
        ),
      );
    }
  };

  return (
    <FavoriteButton
      onClick={handleFavorite}
      aria-label="Favorite"
      aria-pressed={isFavorited}
      data-id={`button-mark-as-favorite-${testId}`}
    >
      {isFavorited ? (
        <HeartIconFilled
          fill={isSong ? 'white' : 'black'}
          width="15px"
          height="17.5px"
          aria-hidden="true"
          data-id="heart-icon-filled"
        />
      ) : (
        <HeartIcon
          fill={isSong ? 'white' : 'black'}
          width="15px"
          height="17.5px"
          aria-hidden="true"
          data-id="heart-icon-outline"
        />
      )}
    </FavoriteButton>
  );
};

export default MarkFavorite;
